<template>
  <div>
    <h3 class="text-h6 mb-4">Configuración de Layout</h3>
    
    <v-alert type="info" text dense class="mb-4">
      Configure la estructura y orden de las secciones del remito.
    </v-alert>

    <!-- Header -->
    <v-card class="mb-4">
      <v-card-title class="subtitle-1">
        <v-icon left>mdi-page-layout-header</v-icon>
        Encabezado
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-switch
              v-model="layoutInterno.header.enabled"
              label="Mostrar encabezado"
              @change="emitirCambios"
            ></v-switch>
          </v-col>
          <v-col cols="12" v-if="layoutInterno.header.enabled">
            <v-select
              v-model="layoutInterno.header.layout"
              :items="layoutsDisponibles"
              label="Distribución del Encabezado"
              outlined
              dense
              @change="emitirCambios"
            ></v-select>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <v-row v-if="layoutInterno.header.enabled">
          <v-col cols="12">
            <h4 class="subtitle-2 mb-2">Zonas del Encabezado</h4>
          </v-col>
          
          <!-- Logo -->
          <v-col cols="12" md="4">
            <v-card outlined>
              <v-card-subtitle>Logo</v-card-subtitle>
              <v-card-text>
                <v-select
                  v-model="layoutInterno.header.zones.logo.position"
                  :items="posiciones"
                  label="Posición"
                  dense
                  @change="emitirCambios"
                ></v-select>
                <v-text-field
                  v-model="layoutInterno.header.zones.logo.width"
                  label="Ancho"
                  dense
                  placeholder="33%"
                  @input="emitirCambios"
                ></v-text-field>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Empresa -->
          <v-col cols="12" md="4">
            <v-card outlined>
              <v-card-subtitle>Datos Empresa</v-card-subtitle>
              <v-card-text>
                <v-select
                  v-model="layoutInterno.header.zones.empresa.position"
                  :items="posiciones"
                  label="Posición"
                  dense
                  @change="emitirCambios"
                ></v-select>
                <v-text-field
                  v-model="layoutInterno.header.zones.empresa.width"
                  label="Ancho"
                  dense
                  placeholder="33%"
                  @input="emitirCambios"
                ></v-text-field>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Remito -->
          <v-col cols="12" md="4">
            <v-card outlined>
              <v-card-subtitle>Datos Remito</v-card-subtitle>
              <v-card-text>
                <v-select
                  v-model="layoutInterno.header.zones.remito.position"
                  :items="posiciones"
                  label="Posición"
                  dense
                  @change="emitirCambios"
                ></v-select>
                <v-text-field
                  v-model="layoutInterno.header.zones.remito.width"
                  label="Ancho"
                  dense
                  placeholder="33%"
                  @input="emitirCambios"
                ></v-text-field>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Secciones -->
    <v-card class="mb-4">
      <v-card-title class="subtitle-1">
        <v-icon left>mdi-view-dashboard</v-icon>
        Secciones del Documento
      </v-card-title>
      <v-card-text>
        <v-alert type="info" text dense class="mb-4">
          Configure qué secciones mostrar y su orden de aparición.
        </v-alert>

        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th>Visible</th>
                <th>Sección</th>
                <th>Orden</th>
                <th>Columnas</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(seccion, key) in seccionesOrdenadas" :key="key">
                <td>
                  <v-checkbox
                    v-model="layoutInterno.sections[key].enabled"
                    hide-details
                    @change="emitirCambios"
                  ></v-checkbox>
                </td>
                <td>
                  <v-chip small :color="seccion.enabled ? 'primary' : 'grey'">
                    {{ getNombreSeccion(key) }}
                  </v-chip>
                </td>
                <td>
                  <v-text-field
                    v-model.number="layoutInterno.sections[key].order"
                    type="number"
                    dense
                    hide-details
                    style="width: 80px;"
                    @input="emitirCambios"
                  ></v-text-field>
                </td>
                <td>
                  <v-select
                    v-model="layoutInterno.sections[key].columns"
                    :items="[1, 2]"
                    dense
                    hide-details
                    style="width: 100px;"
                    @change="emitirCambios"
                  >
                    <template v-slot:selection="{ item }">
                      {{ item === 1 ? 'Completo' : 'Mitad' }}
                    </template>
                    <template v-slot:item="{ item }">
                      {{ item === 1 ? 'Completo' : 'Mitad' }}
                    </template>
                  </v-select>
                </td>
                <td>
                  <v-btn
                    icon
                    small
                    @click="moverSeccionArriba(key)"
                    :disabled="seccion.order === 1"
                  >
                    <v-icon>mdi-arrow-up</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    @click="moverSeccionAbajo(key)"
                    :disabled="seccion.order === 4"
                  >
                    <v-icon>mdi-arrow-down</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>
    </v-card>

    <!-- Footer -->
    <v-card class="mb-4">
      <v-card-title class="subtitle-1">
        <v-icon left>mdi-page-layout-footer</v-icon>
        Pie de Página
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-switch
              v-model="layoutInterno.footer.enabled"
              label="Mostrar pie de página"
              @change="emitirCambios"
            ></v-switch>
          </v-col>
          <v-col cols="12" md="4" v-if="layoutInterno.footer.enabled">
            <v-switch
              v-model="layoutInterno.footer.showBarcode"
              label="Mostrar código de barras"
              @change="emitirCambios"
            ></v-switch>
          </v-col>
          <v-col cols="12" md="4" v-if="layoutInterno.footer.enabled">
            <v-switch
              v-model="layoutInterno.footer.showCAI"
              label="Mostrar CAI"
              @change="emitirCambios"
            ></v-switch>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'LayoutEditor',
  
  props: {
    layout: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      layoutInterno: {
        header: {
          enabled: true,
          layout: 'left-center-right',
          zones: {
            logo: { position: 'left', width: '33%' },
            empresa: { position: 'center', width: '33%' },
            remito: { position: 'right', width: '33%' }
          }
        },
        sections: {
          cliente: { enabled: true, order: 1, columns: 1 },
          entrega: { enabled: true, order: 2, columns: 1 },
          solicitud: { enabled: true, order: 3, columns: 1 },
          transporte: { enabled: true, order: 4, columns: 1 }
        },
        footer: {
          enabled: true,
          showBarcode: true,
          showCAI: true
        }
      },
      layoutsDisponibles: [
        { text: 'Izquierda-Centro-Derecha', value: 'left-center-right' },
        { text: 'Centro', value: 'center' },
        { text: 'Dos Columnas', value: 'two-columns' }
      ],
      posiciones: [
        { text: 'Izquierda', value: 'left' },
        { text: 'Centro', value: 'center' },
        { text: 'Derecha', value: 'right' }
      ],
      nombresSeccion: {
        cliente: 'Cliente',
        entrega: 'Entrega',
        solicitud: 'Solicitud',
        transporte: 'Transporte'
      }
    }
  },

  computed: {
    seccionesOrdenadas() {
      const secciones = { ...this.layoutInterno.sections }
      return Object.keys(secciones)
        .sort((a, b) => secciones[a].order - secciones[b].order)
        .reduce((obj, key) => {
          obj[key] = secciones[key]
          return obj
        }, {})
    }
  },

  watch: {
    layout: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal) {
          this.layoutInterno = JSON.parse(JSON.stringify(newVal))
        }
      }
    }
  },

  methods: {
    getNombreSeccion(key) {
      return this.nombresSeccion[key] || key
    },

    moverSeccionArriba(key) {
      const seccion = this.layoutInterno.sections[key]
      if (seccion.order === 1) return

      // Encontrar la sección con order anterior
      const seccionAnterior = Object.keys(this.layoutInterno.sections).find(
        k => this.layoutInterno.sections[k].order === seccion.order - 1
      )

      if (seccionAnterior) {
        this.layoutInterno.sections[seccionAnterior].order++
        seccion.order--
        this.emitirCambios()
      }
    },

    moverSeccionAbajo(key) {
      const seccion = this.layoutInterno.sections[key]
      if (seccion.order === 4) return

      // Encontrar la sección con order siguiente
      const seccionSiguiente = Object.keys(this.layoutInterno.sections).find(
        k => this.layoutInterno.sections[k].order === seccion.order + 1
      )

      if (seccionSiguiente) {
        this.layoutInterno.sections[seccionSiguiente].order--
        seccion.order++
        this.emitirCambios()
      }
    },

    emitirCambios() {
      this.$emit('update', this.layoutInterno)
    }
  }
}
</script>
